import React, { useId, useState } from "react";
import Moment from "moment";
import ISupplierModel from "../../../models/ISupplierModel";
import OwButton from "../../shared/OwButton/OwButton";
import OwCheckbox from "../../shared/OwCheckbox/OwCheckbox";
import OwDateInput from "../../shared/OwDateInput/OwDateInput";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import OwTitle from "../../shared/OwTitle/OwTitle";
import "./QboBackfillInvoicePaidStatusModal.scss";

/** What the admin chose; everything is optional, an empty set means every linked invoice. */
export type QboBackfillInvoicePaidStatusOptions = {
    startDate?: string;
    endDate?: string;
    orderNo?: string;
    skipPaid: boolean;
};

type Props = {
    supplierModel: ISupplierModel;
    cancelFn: (refreshData?: boolean) => void;
    startFn: (options: QboBackfillInvoicePaidStatusOptions) => void;
};

/**
 * Hidden admin tool: re-read linked QuickBooks Online invoice balances and write the paid status onto the orders.
 * Lets the run be limited to a created-date range or to one OrderWerks order number, so it can be done in batches,
 * and skips orders already flagged paid unless told otherwise.
 */
export const QboBackfillInvoicePaidStatusModal = (props: Props) => {
    const calendarPortalId = useId();
    const [selectedStartDate, setSelectedStartDate] = useState<Moment.Moment | null | undefined>();
    const [selectedEndDate, setSelectedEndDate] = useState<Moment.Moment | null | undefined>();
    const [orderNo, setOrderNo] = useState<string>("");
    const [skipPaid, setSkipPaid] = useState<boolean>(true);
    const connected = !!props.supplierModel.Supplier_Integration?.has_qbo;
    const trimmedOrderNo = orderNo.trim();
    // The dates are not sent with an order number, so a bad range must not block that run.
    const datesInvalid = !trimmedOrderNo && !!selectedStartDate && !!selectedEndDate && selectedEndDate.isBefore(selectedStartDate, "day");

    const start = () => {
        if (!connected || datesInvalid) return;

        props.startFn({
            startDate: !trimmedOrderNo && selectedStartDate ? selectedStartDate.format("YYYY-MM-DD") : undefined,
            endDate: !trimmedOrderNo && selectedEndDate ? selectedEndDate.format("YYYY-MM-DD") : undefined,
            orderNo: trimmedOrderNo || undefined,
            skipPaid,
        });
        props.cancelFn(false);
    };

    return (
        <>
            <div className={"QboBackfillInvoicePaidStatusModal p-3"}>
                <div className={"d-flex justify-content-between"}>
                    <OwTitle tag={"h4"} title={"backfill_qbo_invoice_paid_status"} />
                    <div>
                        <OwButton
                            className={"btn-danger"}
                            centerIconClass={"fas fa-times"}
                            fn={() => {
                                props.cancelFn(false);
                            }}
                        />
                    </div>
                </div>
                <p className={"text-muted small mt-2 mb-0"}>
                    Re-reads the balance of each linked QuickBooks Online invoice and marks the order paid when nothing is owed. Leave the
                    filters empty to run over every linked invoice, or limit the run to a date range or a single order.
                </p>
                {!connected && <div className="alert alert-warning mt-2 mb-0">Connect QuickBooks Online to backfill paid status.</div>}
                <fieldset disabled={!!trimmedOrderNo} className={"border-0 p-0 mx-0 mt-2 mb-0"}>
                    <div className={"row"}>
                        <div className={"col-12 col-md-6 mt-2"}>
                            <OwTitle tag={"h6"} title={"start_date"} defaultMargins={false} />
                            <OwDateInput
                                portalId={calendarPortalId}
                                selectedDate={selectedStartDate}
                                fn={(date) => {
                                    setSelectedStartDate(date);
                                }}
                            />
                        </div>
                        <div className={"col-12 col-md-6 mt-2"}>
                            <OwTitle tag={"h6"} title={"end_date"} defaultMargins={false} />
                            <OwDateInput
                                portalId={calendarPortalId}
                                selectedDate={selectedEndDate}
                                fn={(date) => {
                                    setSelectedEndDate(date);
                                }}
                            />
                        </div>
                    </div>
                    {datesInvalid && <div className={"text-danger small mt-1"}>The end date must be on or after the start date.</div>}
                </fieldset>
                <div className={"mt-2"}>
                    <OwTitle tag={"h6"} title={"order_no"} defaultMargins={false} />
                    <OwFnInput
                        name={"orderNo"}
                        value={orderNo}
                        placeholder={"order_no"}
                        fn={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setOrderNo(e.target.value);
                        }}
                    />
                    <div className={"text-muted small mt-1"}>When an order number is given the dates are ignored.</div>
                </div>
                <div className={"mt-2"}>
                    <OwCheckbox
                        name={"skipPaid"}
                        label={"skip_already_paid_orders"}
                        checked={skipPaid}
                        fn={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setSkipPaid(e.target.checked);
                        }}
                    />
                </div>
                <div className={"mt-3"}>
                    <OwButton title={"start"} fn={start} disabled={!connected || datesInvalid} />
                </div>
            </div>
            <div id={calendarPortalId} className="qbo-backfill-paid-calendar-portal" />
        </>
    );
};
